import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import api from 'services/api'
import useToast from 'hooks/useToast'

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const formatTimestamp = (iso) => {
  if (!iso) return 'Never'
  return new Date(iso).toLocaleString()
}

const WARNING_THRESHOLD = 0.75

const CostBar = ({ label, cost, cap, lastUpdated }) => {
  const ratio = cap > 0 ? Math.min(cost / cap, 1) : 0
  const pct = (ratio * 100).toFixed(1)
  const barColor =
    ratio >= 1 ? 'error' : ratio >= WARNING_THRESHOLD ? 'warning' : 'success'

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="body2">
          {formatCurrency(cost)} / {formatCurrency(cap)} ({pct}%)
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={ratio * 100}
        color={barColor}
        sx={{ height: 12, borderRadius: 1 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        Last updated: {formatTimestamp(lastUpdated)}
      </Typography>
    </Box>
  )
}

const KillSwitch = () => {
  const toast = useToast()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editCap, setEditCap] = useState('')
  const [capDirty, setCapDirty] = useState(false)

  const load = useCallback(async () => {
    try {
      const result = await api.animatedVideos.getKillSwitch()
      setData(result)
      setEditCap(String(result.weekly_cost_cap_dollars))
      setCapDirty(false)
    } catch (e) {
      toast({ message: e.message, severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    load()
  }, [load])

  const handleToggleManual = async () => {
    const newValue = !data.manual_kill_switch
    const confirmed = newValue
      ? window.confirm(
          'This will immediately block ALL new AI animation requests (except staff). Continue?'
        )
      : true
    if (!confirmed) return

    setSaving(true)
    try {
      const result = await api.animatedVideos.updateKillSwitch({
        manual_kill_switch: newValue,
      })
      setData(result)
      toast({
        message: newValue
          ? 'Kill switch ACTIVATED -- new animations blocked'
          : 'Kill switch deactivated -- animations resumed',
        severity: newValue ? 'warning' : 'success',
      })
    } catch (e) {
      toast({ message: e.message, severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveCap = async () => {
    const capVal = parseFloat(editCap)
    if (isNaN(capVal) || capVal <= 0) {
      toast({
        message: 'Weekly cap must be a positive number',
        severity: 'error',
      })
      return
    }

    setSaving(true)
    try {
      const result = await api.animatedVideos.updateKillSwitch({
        weekly_cost_cap_dollars: capVal,
      })
      setData(result)
      setEditCap(String(result.weekly_cost_cap_dollars))
      setCapDirty(false)
      toast({ message: 'Cost cap updated', severity: 'success' })
    } catch (e) {
      toast({ message: e.message, severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 8 }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!data) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Failed to load kill switch data.</Typography>
        <Button onClick={load} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    )
  }

  const anyKillSwitchActive =
    data.manual_kill_switch || data.cost_kill_switch_active

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      {/* A phone has the Cost page's own padding around it. */}
      <Box
        sx={{
          width: { mobile: '100%', desktop: 700 },
          p: { mobile: 0, desktop: 6 },
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          AI Animation Kill Switch
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Controls for blocking new AI animation requests and monitoring weekly
          spend against the cost cap.
        </Typography>

        {/* Status banner */}
        {anyKillSwitchActive && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'error.main',
              color: 'error.contrastText',
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              New AI animations are currently BLOCKED
            </Typography>
            <Typography variant="body2">
              {data.manual_kill_switch && data.cost_kill_switch_active
                ? 'Both manual kill switch and cost cap kill switch are active.'
                : data.manual_kill_switch
                ? 'Manual kill switch is active.'
                : 'Cost cap kill switch triggered automatically.'}
            </Typography>
          </Paper>
        )}

        {/* Manual Kill Switch */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Manual Kill Switch
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Immediately block all new AI animation requests (staff
                bypasses).
              </Typography>
            </Box>
            <Switch
              checked={data.manual_kill_switch}
              onChange={handleToggleManual}
              disabled={saving}
              color="error"
              size="medium"
            />
          </Box>
          {data.manual_kill_switch && data.manual_kill_switch_activated_by && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              Activated by {data.manual_kill_switch_activated_by} at{' '}
              {formatTimestamp(data.manual_kill_switch_activated_at)}
            </Typography>
          )}
        </Paper>

        {/* Cost Kill Switch Status */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Cost Kill Switch
            </Typography>
            <Chip
              label={data.cost_kill_switch_active ? 'TRIGGERED' : 'OK'}
              color={data.cost_kill_switch_active ? 'error' : 'success'}
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Activates automatically when estimated or actual billing reaches the
            weekly cap. Resets each Monday at 00:00 UTC.
            {data.current_week_start && (
              <>
                {' '}
                Current week started {formatTimestamp(data.current_week_start)}.
              </>
            )}
          </Typography>

          <CostBar
            label="Estimated Cost (real-time)"
            cost={data.estimated_week_cost_dollars}
            cap={data.weekly_cost_cap_dollars}
            lastUpdated={data.estimated_cost_last_updated_at}
          />
          <CostBar
            label="Actual GCP Billing (updated every 30 min)"
            cost={data.actual_week_cost_dollars}
            cap={data.weekly_cost_cap_dollars}
            lastUpdated={data.actual_cost_last_updated_at}
          />
        </Paper>

        {/* Cost Cap Setting */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Weekly Cost Cap
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <TextField
              label="Weekly Cost Cap ($)"
              type="number"
              size="small"
              value={editCap}
              onChange={(e) => {
                setEditCap(e.target.value)
                setCapDirty(
                  e.target.value !== String(data.weekly_cost_cap_dollars)
                )
              }}
              sx={{ width: 200 }}
              inputProps={{ min: 0, step: 50 }}
            />
            {capDirty && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveCap}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setEditCap(String(data.weekly_cost_cap_dollars))
                    setCapDirty(false)
                  }}
                  disabled={saving}
                >
                  Discard
                </Button>
              </>
            )}
          </Box>
        </Paper>

        {/* Refresh */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="text" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default KillSwitch
